import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, BarChart3, CalendarDays, Check, CheckCircle2, ChevronLeft, ChevronRight, Clock, Loader2, Lock, Save, Users, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { getCurrentUser } from '@/data/users';
import apiService from '@/services/api';
import WeeklyAttendanceSummary from '@/components/attendance/WeeklyAttendanceSummary';

const API = process.env.REACT_APP_BACKEND_URL;
const PERIODS = [1, 2, 3, 4, 5, 6, 7].map((n) => ({ id: `period${n}`, name: `Period ${n}`, time: ['7:30', '8:30', '10:00', '11:00', '1:00', '1:50', '2:40'][n - 1] }));
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
const STATUS_STYLE = {
  done: 'bg-emerald-100 text-emerald-900 border-emerald-300 hover:bg-emerald-200',
  missing: 'bg-red-50 text-red-800 border-red-300 hover:bg-red-100',
  locked: 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100',
  future: 'bg-white text-gray-400 border-gray-200',
  holiday: 'bg-gray-50 text-gray-400 border-gray-200',
};
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fmtLong = (s) => new Date(`${s}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

const authFetch = async (path, opts = {}) => {
  const res = await fetch(`${API}${path}`, { ...opts, headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}`, 'Content-Type': 'application/json', ...(opts.headers || {}) } });
  if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || `Request failed (${res.status})`); }
  return res.json();
};

function MonthCalendar({ month, days, selected, onSelect, onMonth, loading }) {
  const [y, m] = month.split('-').map(Number);
  const title = new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  const weeks = useMemo(() => {
    const out = []; let week = [null, null, null, null, null];
    days.forEach((d) => {
      if (d.weekday > 4) return;
      if (d.weekday === 0 && week.some(Boolean)) { out.push(week); week = [null, null, null, null, null]; }
      week[d.weekday] = d;
    });
    if (week.some(Boolean)) out.push(week);
    return out;
  }, [days]);
  return (
    <div data-testid="rollcall-calendar">
      <div className="flex items-center justify-between mb-3">
        <Button variant="ghost" size="icon" onClick={() => onMonth(-1)} data-testid="calendar-prev-month"><ChevronLeft className="h-4 w-4" /></Button>
        <p className="font-semibold flex items-center gap-2" data-testid="calendar-month-title">{title}{loading && <Loader2 className="h-3 w-3 animate-spin" />}</p>
        <Button variant="ghost" size="icon" onClick={() => onMonth(1)} data-testid="calendar-next-month"><ChevronRight className="h-4 w-4" /></Button>
      </div>
      <div className="grid grid-cols-5 gap-1 text-center text-xs font-medium text-muted-foreground mb-1">{DAY_LABELS.map((l) => <div key={l}>{l}</div>)}</div>
      <div className="space-y-1">
        {weeks.map((week, i) => (
          <div key={i} className="grid grid-cols-5 gap-1">
            {week.map((d, j) => d ? (
              <button key={d.date} disabled={d.status === 'future' || d.status === 'holiday'} onClick={() => onSelect(d.date)}
                className={`relative h-14 rounded-md border text-sm flex flex-col items-center justify-center transition-colors ${STATUS_STYLE[d.status === 'missing' && !d.editable ? 'locked' : d.status]} ${selected === d.date ? 'ring-2 ring-primary ring-offset-1' : ''}`}
                data-testid={`calendar-day-${d.date}`} data-status={d.status} title={d.status === 'holiday' ? 'School holiday' : d.status}>
                <span className="font-semibold">{Number(d.date.slice(-2))}</span>
                {d.status === 'done' && <span className="text-[10px] leading-none mt-0.5">{d.periods.some((p) => p.period !== 'daily') ? d.periods.map((p) => p.period.replace('period', 'P')).join(' ') : `${d.periods[0].marked} marked`}</span>}
                {d.status === 'missing' && d.editable && <span className="text-[10px] leading-none mt-0.5">missing</span>}
                {!d.editable && d.status !== 'future' && d.status !== 'holiday' && <Lock className="absolute top-1 right-1 h-3 w-3 opacity-60" />}
              </button>
            ) : <div key={j} />)}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-3 mt-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-emerald-200 border border-emerald-300" /> Done</span>
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-red-100 border border-red-300" /> Missing</span>
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-gray-100 border border-gray-200" /> Holiday / future</span>
        <span className="flex items-center gap-1"><Lock className="h-3 w-3" /> Locked (previous term)</span>
      </div>
    </div>
  );
}

export default function RollCallCalendarPage() {
  const user = getCurrentUser();
  const params = new URLSearchParams(window.location.search);
  const qClass = params.get('class') || '';
  const qDate = params.get('date') || '';
  const isPrimary = user?.school_type === 'primary' || /primary|quatui|quirutaro/.test(user?.school_id || '');
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState('');
  const [month, setMonth] = useState((qDate || iso(new Date())).slice(0, 7));
  const [cal, setCal] = useState(null);
  const [selectedDate, setSelectedDate] = useState(qDate || iso(new Date()));
  const [period, setPeriod] = useState('daily');
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [savedExists, setSavedExists] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showWeekly, setShowWeekly] = useState(false);
  const [calLoading, setCalLoading] = useState(false);
  const reqRef = useRef(0);

  useEffect(() => {
    const isAdmin = user?.email?.toLowerCase() === 'roytari@spc.edu.vu' || user?.role === 'admin';
    authFetch(`/api/classes/?skip_counts=true${isAdmin ? '&scope=school' : ''}`).then((cs) => {
      let list = cs;
      if (isPrimary) {
        const rank = (c) => (c.teacher_id ? 0 : 2) - (c.subject === 'General' ? 1 : 0);
        const byYear = {};
        [...cs].sort((a, b) => rank(a) - rank(b) || a.id.localeCompare(b.id)).forEach((c) => { if (c.year >= 1 && c.year <= 6 && !byYear[c.year]) byYear[c.year] = { ...c, name: `Year ${c.year}` }; });
        list = Object.values(byYear).sort((a, b) => a.year - b.year);
      } else {
        list = [...cs].sort((a, b) => (a.year - b.year) || a.name.localeCompare(b.name));
      }
      setClasses(list);
      setClassId((c) => c || (qClass && (list.find((x) => x.id === qClass) || (isPrimary && list.find((x) => x.year === cs.find((y) => y.id === qClass)?.year)))?.id) || list[0]?.id || '');
      setPeriod(isPrimary ? 'daily' : 'period1');
    }).catch(() => toast.error('Failed to load classes'));
  }, [isPrimary]);

  const loadCalendar = useCallback(() => {
    if (!classId) return;
    const id = ++reqRef.current;
    setCalLoading(true);
    authFetch(`/api/student-attendance/calendar?class_id=${classId}&month=${month}`)
      .then((d) => { if (id === reqRef.current) setCal(d); })
      .catch((e) => toast.error(e.message))
      .finally(() => { if (id === reqRef.current) setCalLoading(false); });
  }, [classId, month]);
  useEffect(() => { loadCalendar(); }, [loadCalendar]);

  useEffect(() => {
    if (!classId || !selectedDate) return;
    setLoading(true);
    Promise.all([
      authFetch(`/api/classes/${classId}/students`),
      authFetch(`/api/student-attendance/class/${classId}?date_str=${selectedDate}&period=${period}`),
    ]).then(([studs, saved]) => {
      setStudents(studs);
      const map = {};
      studs.forEach((s) => { map[s.id] = 'present'; });
      saved.records.forEach((r) => { map[r.student_id] = r.status; });
      setAttendance(map);
      setSavedExists(saved.records.length > 0);
    }).catch((e) => toast.error(e.message)).finally(() => setLoading(false));
  }, [classId, selectedDate, period]);

  const dayInfo = cal?.days.find((d) => d.date === selectedDate);
  const inCurrentTerm = cal ? selectedDate >= cal.current_term.start && selectedDate <= cal.current_term.end : true;
  const editable = dayInfo ? dayInfo.editable : (!cal || cal.can_edit_any_date || inCurrentTerm) && (!cal || selectedDate <= cal.today);
  const cls = classes.find((c) => c.id === classId);

  const changeMonth = (delta) => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };
  const jumpTo = (dateStr) => { setMonth(dateStr.slice(0, 7)); setSelectedDate(dateStr); };

  const save = async () => {
    if (!cls) return;
    const records = students.map((s) => ({ student_id: s.id, status: attendance[s.id] || 'present' }));
    setSaving(true);
    try {
      const r = await apiService.saveStudentAttendance({ class_id: classId, date: selectedDate, period, attendance: records });
      toast.success(`${cls.name} — ${fmtLong(selectedDate)} saved (${r.created} new, ${r.updated} updated)`);
      setSavedExists(true);
      loadCalendar();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const stats = useMemo(() => {
    const v = Object.values(attendance);
    return { present: v.filter((x) => x === 'present').length, absent: v.filter((x) => x === 'absent').length, late: v.filter((x) => x === 'late').length };
  }, [attendance]);
  const markAll = (status) => setAttendance(Object.fromEntries(students.map((s) => [s.id, status])));

  return (
    <div className="space-y-6" data-testid="rollcall-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><CalendarDays className="h-6 w-6 text-primary" /> Roll Call</h1>
          <p className="text-sm text-muted-foreground">Pick any day on the calendar to fill in or correct attendance. Red days are missing roll calls{cal ? ` for ${cal.current_term.name}` : ''}.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={classId} onChange={(e) => setClassId(e.target.value)} className="h-10 border rounded-md px-3 bg-background text-sm min-w-[200px]" data-testid="rollcall-class-select">
            {classes.map((c) => <option key={c.id} value={c.id}>{c.name}{c.teacher_name && c.teacher_id !== user?.id ? ` — ${c.teacher_name}` : ''}</option>)}
          </select>
          <Button variant="outline" onClick={() => setShowWeekly(!showWeekly)} className="gap-2" data-testid="rollcall-weekly-toggle"><BarChart3 className="h-4 w-4" /> Weekly summary</Button>
        </div>
      </div>

      {showWeekly && cls && <WeeklyAttendanceSummary classId={classId} className={cls.name} />}

      <div className="grid lg:grid-cols-5 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center justify-between">
              <span>Calendar</span>
              {cal && <Badge variant={cal.term_missing.length ? 'destructive' : 'default'} className={cal.term_missing.length ? '' : 'bg-emerald-600'} data-testid="term-missing-badge">{cal.term_missing.length ? `${cal.term_missing.length} missing in ${cal.current_term.name}` : `${cal.current_term.name} complete`}</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {cal ? <MonthCalendar month={month} days={cal.days} selected={selectedDate} onSelect={setSelectedDate} onMonth={changeMonth} loading={calLoading} /> : <Loader2 className="h-5 w-5 animate-spin mx-auto" />}
            {cal?.term_missing.length > 0 && (
              <div className="mt-4" data-testid="term-missing-list">
                <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1"><AlertCircle className="h-3 w-3 text-red-600" /> Missed days this term — tap to fill in</p>
                <div className="flex flex-wrap gap-1 max-h-28 overflow-y-auto">
                  {cal.term_missing.map((d) => <button key={d} onClick={() => jumpTo(d)} className="text-xs border border-red-200 bg-red-50 text-red-800 rounded px-2 py-0.5 hover:bg-red-100" data-testid={`missing-chip-${d}`}>{new Date(`${d}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</button>)}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-3">
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <CardTitle className="text-base" data-testid="selected-date-title">{fmtLong(selectedDate)}</CardTitle>
                <p className="text-xs text-muted-foreground">{cls?.name} · {students.length} students · {savedExists ? <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> saved</span> : <span className="text-red-700">not yet saved</span>}</p>
              </div>
              <div className="flex gap-2 text-xs">
                <Badge className="bg-emerald-600">{stats.present} present</Badge>
                <Badge variant="destructive">{stats.absent} absent</Badge>
                <Badge className="bg-amber-500">{stats.late} late</Badge>
              </div>
            </div>
            {!isPrimary && (
              <div className="flex flex-wrap gap-1 mt-3" data-testid="period-selector">
                {PERIODS.map((p) => {
                  const done = dayInfo?.periods.some((x) => x.period === p.id);
                  return <button key={p.id} onClick={() => setPeriod(p.id)} className={`text-xs rounded-full border px-3 py-1 flex items-center gap-1 ${period === p.id ? 'bg-primary text-primary-foreground border-primary' : done ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-background'}`} data-testid={`period-btn-${p.id}`}>{done && <Check className="h-3 w-3" />}{p.name} <span className="opacity-60">{p.time}</span></button>;
                })}
              </div>
            )}
            {!editable && <p className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 flex items-center gap-2" data-testid="locked-notice"><Lock className="h-3 w-3" /> This day is in a previous term and is locked for teachers. Ask the principal to make changes.</p>}
          </CardHeader>
          <CardContent className="p-0">
            {editable && students.length > 0 && (
              <div className="px-4 pb-3 flex gap-2">
                <Button size="sm" variant="outline" onClick={() => markAll('present')} data-testid="mark-all-present">All present</Button>
                <Button size="sm" variant="outline" onClick={() => markAll('absent')} data-testid="mark-all-absent">All absent</Button>
                {saving && <span className="text-xs text-muted-foreground flex items-center gap-1 ml-2"><Loader2 className="h-3 w-3 animate-spin" /> Saving {students.length} students…</span>}
              </div>
            )}
            {loading ? <div className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div> : (
              <ul className="divide-y max-h-[520px] overflow-y-auto" data-testid="rollcall-student-list">
                {students.map((s, i) => (
                  <li key={s.id} className="flex items-center gap-3 px-4 py-2" data-testid={`rollcall-row-${s.id}`}>
                    <span className="text-xs text-muted-foreground w-6">{i + 1}</span>
                    <span className="flex-1 text-sm font-medium truncate">{s.name}</span>
                    <div className="flex gap-1">
                      {[['present', CheckCircle2, 'bg-emerald-600 text-white', 'text-emerald-700 border-emerald-300'], ['absent', XCircle, 'bg-red-600 text-white', 'text-red-700 border-red-300'], ['late', Clock, 'bg-amber-500 text-white', 'text-amber-700 border-amber-300']].map(([st, Icon, on, off]) => (
                        <button key={st} disabled={!editable} onClick={() => setAttendance((a) => ({ ...a, [s.id]: st }))}
                          className={`h-8 px-2 rounded-md border text-xs flex items-center gap-1 capitalize disabled:opacity-50 ${attendance[s.id] === st ? on : `bg-background ${off}`}`} data-testid={`status-${st}-${s.id}`}>
                          <Icon className="h-3.5 w-3.5" /><span className="hidden sm:inline">{st}</span>
                        </button>
                      ))}
                    </div>
                  </li>
                ))}
                {students.length === 0 && <li className="p-6 text-center text-sm text-muted-foreground flex items-center justify-center gap-2"><Users className="h-4 w-4" /> No students in this class.</li>}
              </ul>
            )}
            {editable && students.length > 0 && (
              <div className="p-4 border-t flex justify-end">
                <Button onClick={save} disabled={saving} className="gap-2" data-testid="rollcall-save-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {savedExists ? 'Update roll call' : 'Save roll call'}</Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
